import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import { DataGrid, gridClasses, GridColDef } from '@mui/x-data-grid';
import { useEffect, useState, useCallback } from 'react';
import { localDateFormat } from '../../../utils/timeFormat';
import { RootState } from '../../../reducers';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import Loading from '../../Shared/Loading';
import phoneFormat from '../../../utils/phoneFormat';
import { setMemberDetailFilter, fetchMemberDetailReport, exportMemberDetailsReport, setPageNumber } from '../../../store/memberDetailsReport.slice';
import { isDefaultDate } from '../../../utils/common';
import { MemberStatusEnum } from '../../../Enum/MemberStatusEnum';
import { fetchPopulationList } from '../../../store/adminMembers.slice';

const MemberDetailsReport = () => {
    const { memberDetailReport, memberDetailFilter, pageNumber, pageTotal } = useSelector((state: RootState) => state.memberDetailsReport);
    const { downLoading } = useSelector((state: RootState) => state.shared);
    const dispatch = useAppDispatch();

    const memberStatus = [
        { name: 'All', filter: '-1' },
        { name: 'Active & Undetermined', filter: MemberStatusEnum.Active.toString() + ',' + MemberStatusEnum.Undetermined.toString() },
        { name: 'Undetermined', filter: MemberStatusEnum.Undetermined.toString() },
        { name: 'Active', filter: MemberStatusEnum.Active.toString() },
        { name: 'Associate', filter: MemberStatusEnum.Associate.toString() },
        { name: 'Scheduled', filter: MemberStatusEnum.Scheduled.toString() },
        { name: 'UncompensatedCare', filter: MemberStatusEnum.UncompensatedCare.toString() },
        { name: 'HRA In Review', filter: MemberStatusEnum.HRAInReview.toString() },
        { name: 'FUHFUM In Progress', filter: MemberStatusEnum.FUHFUMInProgress.toString() },
        { name: 'Lost Insurance Coverage', filter: MemberStatusEnum.LostInsuranceCoverage.toString() },
        { name: 'Preparing For Graduation', filter: MemberStatusEnum.PreparingForGraduation.toString() },
        { name: 'Suspended', filter: MemberStatusEnum.Suspended.toString() },
        { name: 'Incarcerated', filter: MemberStatusEnum.Incarcerated.toString() },
        { name: 'Disenrolled From HRA', filter: MemberStatusEnum.DisenrolledFromHRA.toString() },
        { name: 'Disenrolled From Health Plan', filter: MemberStatusEnum.DisenrolledFromHealthPlan.toString() },
        { name: 'Refused To Continue Services', filter: MemberStatusEnum.RefusedToContinueServices.toString() },
        { name: 'Unreachable', filter: MemberStatusEnum.Unreachable.toString() },
        { name: 'Deceased', filter: MemberStatusEnum.Deceased.toString() },
        { name: 'Disenrolled From CHP', filter: MemberStatusEnum.DisenrolledFromCHP.toString() },
        { name: 'Refused To Enroll', filter: MemberStatusEnum.RefusedToEnroll.toString() },
        { name: 'FUHFUM Close', filter: MemberStatusEnum.FUHFUMClose.toString() },
        { name: 'FUH Closed Billable', filter: MemberStatusEnum.FUHClosedBillable.toString() },
        { name: 'FUH Closed Unbillable', filter: MemberStatusEnum.FUHClosedUnbillable.toString() },
    ];

    const columns: GridColDef[] = [
        {
            field: 'AhcccsId',
            headerName: 'AHCCCS Id',
            minWidth: 110,
            flex: 1,
        },
        {
            field: 'LastName',
            headerName: 'Last Name',
            minWidth: 180,
            flex: 1,
        },
        {
            field: 'FirstName',
            headerName: 'First Name',
            minWidth: 160,
            flex: 1,
        },
        {
            field: 'DOB',
            headerName: 'DOB',
            minWidth: 120,
            flex: 1,
            valueGetter: (value, row) => (isDefaultDate(row.DOB) ? '-' : localDateFormat(new Date(row.DOB))),
        },
        {
            field: 'Status',
            headerName: 'Status',
            minWidth: 180,
            flex: 1,
        },
        {
            field: 'HealthPlan',
            headerName: 'Health Plan',
            minWidth: 160,
            flex: 1,
        },
        {
            field: 'Population',
            headerName: 'Population',
            minWidth: 160,
            flex: 1,
        },
        {
            field: 'PrimaryProvider',
            headerName: 'Primary Provider',
            width: 180,
        },
        {
            field: 'TeamName',
            headerName: 'Team',
            minWidth: 160,
            flex: 1,
        },
        {
            field: 'Address1',
            headerName: 'Address',
            minWidth: 160,
            flex: 1,
        },
        {
            field: 'City',
            headerName: 'City',
            minWidth: 110,
            flex: 1,
        },
        {
            field: 'State',
            headerName: 'State',
            minWidth: 90,
            flex: 1,
        },
        {
            field: 'ZipCode',
            headerName: 'ZipCode',
            minWidth: 110,
            flex: 1,
        },
        {
            field: 'TabletId',
            headerName: 'Tablet #',
            minWidth: 160,
            flex: 1,
        },
        {
            field: 'CCTeam',
            headerName: 'CC',
            minWidth: 160,
            flex: 1,
        },
        {
            field: 'NPTeam',
            headerName: 'NP',
            minWidth: 160,
            flex: 1,
        },
        {
            field: 'BHTeam',
            headerName: 'BH',
            minWidth: 160,
            flex: 1,
        },
        {
            field: 'CLTeam',
            headerName: 'CL',
            minWidth: 160,
            flex: 1,
        },
        {
            field: 'PrimaryPhone',
            headerName: 'Primary Phone',
            minWidth: 180,
            flex: 1,
            valueGetter: (value, row) =>
                row.PrimaryPhone === null || phoneFormat(row.PrimaryPhone) === null ? 'n/a' : phoneFormat(row.PrimaryPhone),
        },
        {
            field: 'SecondaryPhone',
            headerName: 'SecondaryPhone',
            minWidth: 180,
            flex: 1,
            valueGetter: (value, row) =>
                row.SecondaryPhone === null || phoneFormat(row.SecondaryPhone) === null ? 'n/a' : phoneFormat(row.SecondaryPhone),
        },
    ];

    const [pageSize, setPageSize] = useState(15);
    const handleWindowResize = useCallback(() => {
        setPageSize(parseInt(window.innerHeight / (window.innerHeight > 1000 ? 72 : 74) + ''));
    }, []);
    useEffect(() => {
        handleWindowResize();
        dispatch(fetchPopulationList());
        window.addEventListener('resize', handleWindowResize, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Grid container>
            <Box width={'100%'} borderBottom={1} paddingBottom={1} marginBottom={1}>
                <Typography fontWeight="fontWeightMedium" component="h3">
                    Member Details Report
                </Typography>
            </Box>
            {<Loading message={'Loading...'} />}
            <Grid size={12}>
                <Stack width="100%" direction="row" alignItems="center" marginY={2} className="float-right" spacing={3}>
                    <FormControl className="form-control-200">
                        <InputLabel id="demo-simple-select-label">Filter By</InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            value={memberDetailFilter}
                            label="Filter By"
                            onChange={(e) => {
                                dispatch(setMemberDetailFilter(e.target.value));
                                dispatch(fetchMemberDetailReport(e.target.value));
                            }}
                        >
                            <MenuItem disabled value={'-2'}>
                                Select Status
                            </MenuItem>
                            {memberStatus.map((status) => (
                                <MenuItem key={`status-${status.filter}`} value={status.filter}>
                                    {status.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        style={{ minWidth: '120px', marginLeft: 'auto' }}
                        onClick={() => dispatch(exportMemberDetailsReport(memberDetailFilter))}
                        variant="contained"
                        color="success"
                        disabled={downLoading}
                    >
                        {downLoading ? 'Exporting...' : 'Export'}
                    </Button>
                </Stack>
            </Grid>
            <Grid style={{ height: '84vh', width: '100%' }}>
                <DataGrid
                    rowCount={pageTotal}
                    rows={memberDetailReport}
                    columns={columns}
                    autoHeight
                    paginationModel={{ pageSize: pageSize, page: pageNumber }}
                    pageSizeOptions={[5]}
                    paginationMode="server"
                    onPaginationModelChange={(model) => {
                        setPageSize(model.pageSize);
                        dispatch(setPageNumber(model.page));
                        dispatch(fetchMemberDetailReport(memberDetailFilter));
                    }}
                    getRowId={(row) => row.Id}
                    getRowHeight={() => 'auto'}
                    sx={{
                        [`& .${gridClasses.cell}`]: {
                            py: 1,
                        },
                    }}
                />
            </Grid>
        </Grid>
    );
};

export default MemberDetailsReport;
